/* Allure Services — Phase 2 editorial revamp.
   Cream paper, 3-column editorial menu, hairline rules between columns. */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { tenantHref } from "@/lib/tenant";
import {
  ChapterEyebrow, DisplayHeadline, GoldRule, FilmGrain, EditorialNumeral, HairlineButton,
} from "@/components/editorial";
import useEditorialReveal from "@/hooks/useEditorialReveal";
import useStructuredData from "@/hooks/useStructuredData";
import { allureLocalBusinessSchema, allureServicesCatalogSchema } from "@/lib/schemas";

const PLACEHOLDERS = [
  { slug: "editorial-portrait",  name: "Editorial Portrait Sitting", description: "A 90-minute studio sitting for a refined personal portrait. Includes wardrobe consultation and a curated print credit.", price_from: 1400 },
  { slug: "brand-story-halfday", name: "Brand Story Half-Day",       description: "Four hours on location capturing your craft — product, process, portrait. Delivered as a small editorial set.",       price_from: 2200 },
  { slug: "milestone-session",   name: "Milestone Session",          description: "For weddings, anniversaries, family editions. A full day of unhurried storytelling, paired with an heirloom album.",   price_from: 3200 },
];

// Compute starting-at price: take the lowest non-zero tier price.
// Falls back to flat price/price_from/starting_price fields when no tiers exist.
function startingPrice(svc) {
  if (Array.isArray(svc?.tiers) && svc.tiers.length > 0) {
    const prices = svc.tiers
      .map((t) => Number(t?.price))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (prices.length) return Math.min(...prices);
  }
  const flat = svc?.price_from ?? svc?.starting_price ?? svc?.price;
  const n = Number(flat);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatPrice(n) {
  if (n == null) return null;
  // Drop trailing .00 for clean editorial typesetting
  return Number.isInteger(n) ? `$${n.toLocaleString("en-US")}` : `$${n.toFixed(2).replace(/\.00$/, "")}`;
}

export default function Services() {
  const [services, setServices] = useState([]);
  useEditorialReveal();
  useStructuredData([allureLocalBusinessSchema(), allureServicesCatalogSchema()]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => setServices([]));
  }, []);
  const list = useMemo(() => (services.length > 0 ? services : PLACEHOLDERS), [services]);

  return (
    <div className="relative bg-[color:var(--cream)] text-[color:var(--cream-ink)] min-h-screen">
      <FilmGrain opacity={0.04} tone="warm" />
      <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <GoldRule width="2rem" />
        <div className="mt-3"><ChapterEyebrow chapter="II" label="Services" /></div>
        <DisplayHeadline size="hero" className="mt-8 max-w-3xl" data-reveal="rise">
          A short menu, kept by <em>hand</em>.
        </DisplayHeadline>
        <p className="font-serif italic text-xl md:text-2xl mt-8 text-[color:var(--cream-ink)]/70 max-w-xl" data-reveal="rise">
          Considered sessions, each priced honestly. By appointment only.
        </p>

        {/* Editorial preface — extended prose about how we price, what's
            standard across every booking, and who the menu is built for.
            Lifts the page from a price card into a brochure-grade
            commissioning guide. */}
        <div className="mt-12 lg:mt-14 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 max-w-[1240px]" data-reveal="rise">
          <div>
            <p className="ed-body-lg text-[color:var(--cream-ink)]/80 leading-relaxed text-lg">
              The menu is short on purpose. Allure Image takes on roughly thirty editorial sittings a year — enough work to keep the studio sharp, few enough that each booking gets the studio's full attention from brief through to printed delivery. The packages below are the four formats we know inside out; they're the way we'd recommend a friend commission a session if they didn't know where to start.
            </p>
            <p className="mt-6 text-[color:var(--cream-ink)]/72 leading-relaxed">
              Prices are quoted as starting figures — they cover the photographer's time, post-production, a curated edit, and the digital delivery file. Wardrobe consultation, hair-and-makeup referrals, location scouting, and print credits are included as noted on each package. We don't run upsell tiers. If a particular sitting needs something the menu doesn't list (a studio rental, a second photographer, a video pass), we quote it at-cost on the call.
            </p>
            <p className="mt-6 text-[color:var(--cream-ink)]/72 leading-relaxed">
              Every reservation is held with a small Zelle deposit (twenty percent of the package) and confirmed by a written agreement we send via email. Sessions are held Tuesday through Saturday between 10am and 6pm at our daylight studio in Newark, or on location within the New York / New Jersey / Philadelphia tri-state. Out-of-region commissions are accepted on case-by-case; travel is billed transparently and never marked up.
            </p>
          </div>

          {/* What's included in every sitting — the trust-signal table. */}
          <aside className="bg-[color:var(--cream-paper)] border border-[color:var(--cream-ink)]/12 p-7 lg:p-9" data-testid="services-included">
            <div className="ed-eyebrow text-[color:var(--gold-deep)]">Included in every sitting</div>
            <GoldRule width="2rem" className="mt-3 mb-6" />
            <ul className="space-y-4 text-[color:var(--cream-ink)]/85 leading-relaxed">
              {[
                ["Pre-session call.", "A fifteen-minute call about wardrobe, mood, and the shape of the morning."],
                ["Wardrobe consultation.", "A written brief naming three or four looks that flatter the light and the room."],
                ["Hair-and-makeup referral.", "Direct intros to the artists we've vetted across NJ and Manhattan."],
                ["Daylight studio.", "Use of our Newark daylight room is included on every studio package."],
                ["Editorial edit.", "Roughly twenty cinema-graded RAW conversions delivered as high-res JPEGs."],
                ["Print credit.", "A studio print credit (eight-by-ten or larger) included on every booking above the portrait tier."],
                ["Archive copy.", "Negatives kept in two locations for the working life of the studio."],
              ].map(([label, body], i) => (
                <li key={i} className="grid grid-cols-[auto_1fr] gap-x-3">
                  <span className="text-[color:var(--gold-deep)] font-serif italic">·</span>
                  <span>
                    <span className="font-serif italic text-[color:var(--cream-ink)] text-lg">{label}</span>{" "}
                    <span className="text-[color:var(--cream-ink)]/70">{body}</span>
                  </span>
                </li>
              ))}
            </ul>
          </aside>
        </div>

        <div className="mt-16 lg:mt-24 grid md:grid-cols-3 gap-y-14">
          {list.map((s, i) => {
            const price = startingPrice(s);
            const priceLabel = formatPrice(price);
            return (
              <div
                key={s.id || s.slug || i}
                data-reveal="rise"
                data-testid={`service-card-${i}`}
                className={`px-6 lg:px-10 ${i % 3 !== 0 ? "md:border-l border-[color:var(--cream-ink)]/12" : ""}`}
              >
                <EditorialNumeral value={String(i + 1).padStart(2, "0")} size="lg" style="outline" tone="gold" />
                <h3 className="font-serif text-2xl md:text-3xl mt-6 leading-tight">{s.name || s.title}</h3>
                <GoldRule width="2rem" className="mt-5 mb-5" />
                <p className="ed-body-lg text-[color:var(--cream-ink)]/75 leading-relaxed">{s.description || s.summary}</p>
                <p
                  className="font-serif italic mt-5 text-lg text-[color:var(--cream-ink)]"
                  data-testid={`service-price-${i}`}
                >
                  {priceLabel ? <>starting at <span className="text-[color:var(--gold-deep)]">{priceLabel}</span></> : <span className="text-[color:var(--cream-ink)]/65">by request</span>}
                </p>
                <Link to={tenantHref(`/booking?service=${s.slug || s.id || ""}`)}
                  className="mt-5 inline-block ed-eyebrow text-[color:var(--gold-base)] hover:text-[color:var(--gold-deep)] border-b border-[color:var(--gold-base)]/45 hover:border-[color:var(--gold-deep)] pb-0.5">
                  Reserve →
                </Link>
              </div>
            );
          })}
        </div>

        <div className="mt-20 flex items-center justify-between gap-6 flex-wrap">
          <p className="font-serif italic text-lg text-[color:var(--gold-deep)]">by appointment only.</p>
          <HairlineButton href={tenantHref("/booking")}>Reserve a sitting</HairlineButton>
        </div>

        {/* How a session unfolds — 3-step process explainer.
            Reuses the chapter-heading + numeral pattern from the home page
            so the visual rhythm stays consistent. Adds ~150 substantive
            words for SEO + sets clear expectations for first-time clients. */}
        <section className="mt-24 lg:mt-32 pt-16 border-t border-[color:var(--cream-ink)]/12" data-testid="services-how-it-unfolds">
          <ChapterEyebrow label="How a session unfolds" />
          <GoldRule width="2rem" className="mt-3 mb-7" />
          <DisplayHeadline size="md" className="max-w-3xl" data-reveal="rise">
            From the first letter to the printed plate.
          </DisplayHeadline>
          <div className="mt-12 grid md:grid-cols-3 gap-y-12">
            {[
              {
                n: "01",
                title: "Letter & brief.",
                body: "After you reserve, we trade a few notes by email about what you'd like remembered — the room, the wardrobe, the people in the frame. Most briefs settle within two short replies. A pre-session call follows in the week before the booking to nail down call-times, the look, and any logistical knots.",
              },
              {
                n: "02",
                title: "The sitting.",
                body: "On the day, we arrive twenty minutes before the call-time to set the room and unpack the kit. The actual photography is unhurried — ninety minutes for a studio portrait, four hours for a brand half-day, a full day for a milestone. There's no choreography sheet; the morning is shaped around what's working in the light.",
              },
              {
                n: "03",
                title: "The edit & delivery.",
                body: "The cinema-graded set arrives within two weeks (one week for studio portraits) as a private viewing room — passworded, downloadable, kept online for a year. Print orders and album builds run on a separate two-to-three week timeline. Originals are archived to two locations and held for the working life of the studio.",
              },
            ].map((step, i) => (
              <div
                key={step.n}
                data-reveal="rise"
                className={`px-6 lg:px-10 ${i > 0 ? "md:border-l border-[color:var(--cream-ink)]/12" : ""}`}
                data-testid={`services-step-${i}`}
              >
                <EditorialNumeral value={step.n} size="lg" style="outline" tone="gold" />
                <h3 className="font-serif text-2xl md:text-3xl mt-6 leading-tight">{step.title}</h3>
                <GoldRule width="2rem" className="mt-5 mb-5" />
                <p className="ed-body-lg text-[color:var(--cream-ink)]/75 leading-relaxed">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Closing note + commissioning prompt. */}
        <section className="mt-24 lg:mt-32 pt-14 border-t border-[color:var(--cream-ink)]/12 text-center max-w-3xl mx-auto" data-testid="services-closing-note">
          <div className="ed-eyebrow text-[color:var(--gold-deep)]">A note on bespoke commissions</div>
          <GoldRule width="2rem" align="center" className="mt-3 mb-6" />
          <p className="ed-body-lg text-[color:var(--cream-ink)]/75 leading-relaxed">
            If none of the four formats above fits — a multi-day campaign, a book project, a printed annual report for the family, a destination wedding — write to us with a one-paragraph brief and we'll come back with a tailored proposal within two business days. We take on roughly four bespoke commissions a year and price them per-project rather than per-day.
          </p>
          <div className="mt-9">
            <HairlineButton href={tenantHref("/contact")}>Write to the studio</HairlineButton>
          </div>
        </section>
      </div>
    </div>
  );
}
